'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useFplLeagueSearch, syncFplLeague, FplApiError } from '@/lib/api/fpl-routes'
import { Spinner } from '@/components/ui/spinner'
import { pinScrollOnFocus } from '@/lib/utils'

export function FplLeaguesPage() {
  const [nameQuery, setNameQuery] = useState('')
  const [idInput, setIdInput] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [selected, setSelected] = useState(false)
  const router = useRouter()

  const { data, isLoading, isFetching } = useFplLeagueSearch(nameQuery)
  const results = selected ? [] : (data?.data ?? [])

  async function openLeague(leagueId: number) {
    setSelected(true)
    setSyncing(true)
    setSyncError(null)
    try {
      await syncFplLeague(leagueId)
      router.push(`/fpl/leagues/${leagueId}`)
    } catch (err) {
      setSyncError(err instanceof FplApiError ? err.message : 'Failed to load that FPL league.')
      setSelected(false)
    } finally {
      setSyncing(false)
    }
  }

  function submitId(e: React.FormEvent) {
    e.preventDefault()
    const id = Number(idInput.trim())
    if (Number.isFinite(id) && id > 0) openLeague(id)
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 py-6 text-center">
      <h2 className="text-xl font-semibold text-white">Search for an FPL league</h2>

      <div className="w-full max-w-xs">
        <input
          type="text"
          value={nameQuery}
          onChange={(e) => {
            setNameQuery(e.target.value)
            setSelected(false)
          }}
          onFocus={pinScrollOnFocus}
          placeholder="Search by league name…"
          className="w-full rounded-md border border-white/10 bg-black px-3 py-2 text-center text-sm text-white outline-none placeholder:text-white/30 focus:border-white/30"
        />
      </div>

      {nameQuery.trim().length >= 2 && (isLoading || isFetching) && (
        <div className="flex justify-center py-2">
          <Spinner className="size-5 text-white/50" />
        </div>
      )}

      {nameQuery.trim().length >= 2 && !isLoading && results.length === 0 && (
        <p className="text-sm text-white/40">No leagues found — try the league ID below.</p>
      )}

      {results.length > 0 && (
        <div className="w-full max-w-xs rounded-xl border border-white/10 bg-[#0e1114] text-left">
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              disabled={syncing}
              onClick={() => openLeague(r.id)}
              style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
              className="flex w-full animate-in items-center justify-between gap-3 border-b border-white/10 px-3 py-2 text-left fade-in-0 slide-in-from-bottom-1 transition-colors duration-200 ease-out last:border-b-0 hover:bg-white/5 disabled:opacity-50"
            >
              <span className="truncate text-sm text-white">{r.name}</span>
              {r.member_count != null && (
                <span className="shrink-0 text-xs text-white/40">{r.member_count.toLocaleString()} players</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex w-full max-w-xs items-center gap-2 text-xs text-white/30">
        <span className="h-px flex-1 bg-white/10" />
        can&apos;t find the league? enter its ID
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <form onSubmit={submitId} className="flex w-full max-w-xs flex-col items-center gap-3">
        <input
          type="text"
          inputMode="numeric"
          value={idInput}
          onChange={(e) => setIdInput(e.target.value)}
          onFocus={pinScrollOnFocus}
          placeholder="Enter the FPL league ID…"
          className="w-full rounded-md border border-white/10 bg-black px-3 py-2 text-center text-sm text-white outline-none placeholder:text-white/30 focus:border-white/30"
        />
        <button
          type="submit"
          disabled={syncing || !idInput.trim()}
          className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          Go to league
        </button>
      </form>

      {syncing && (
        <div className="flex flex-col items-center gap-2 py-2">
          <Spinner className="size-5 text-white/50" />
          <p className="text-sm text-white/40">Loading league…</p>
        </div>
      )}

      {syncError && <p className="text-sm text-red-400">{syncError}</p>}
    </div>
  )
}
